// @ts-nocheck
"use strict";

import { createBackup, downloadBackup, parseBackup } from "./backup";
import {
  createCustomFood,
  getActiveCatalogVersion,
  initializeCatalog,
  searchCatalog,
  searchOnlineBrandedFoods
} from "./catalog";
import {
  createLoggedFoodEntry,
  csvCell as safeCsvCell,
  getOffPlanEntries,
  normalizeAppState,
  normalizeSearchText,
  offPlanTotals
} from "./domain";
import { createStateStore } from "./storage";

const APP_VERSION = __APP_VERSION__;
const stateStore = await createStateStore();
let storageWorks = stateStore.durable;
let S = stateStore.state;
const save = ()=>{
  S.updatedAt = new Date().toISOString();
  void stateStore.save(S).catch(()=>{ storageWorks=false; toast("Storage failed — export a backup now"); });
};

/* ---------------- plan data ---------------- */
const START_W = 93.0;
const TARGET_KCAL = 1450, TARGET_P = 175, STEP_GOAL = 8000;
const BOTTLE_L = 0.8, WATER_GOAL = 4, WATER_MAX = 6;   // 800 ml bottles · 4 = 3.2 L minimum · +2 buffer
const MEASURE_INFO = {
  w:{type:"w", name:"Weight", card:"wCard", badge:"wDue"},
  st:{type:"st", name:"Steps", card:"stCard", badge:"stDue"},
  cs:{type:"cs", name:"Waist", card:"csCard", badge:"csDue"}
};
const RANKS = [
  {level:1,  threshold:93.00, name:"Day One",              badge:"Levels%20and%20Badges/Welcome%20Rank!.png", prize:"Welcome"},
  {level:2,  threshold:91.92, name:"Warming Up",           badge:"Levels%20and%20Badges/50.png",   prize:"50 EGP"},
  {level:3,  threshold:90.83, name:"Locked In",            badge:"Levels%20and%20Badges/100.png",  prize:"100 EGP"},
  {level:4,  threshold:89.75, name:"The Grind",            badge:"Levels%20and%20Badges/200.png",  prize:"200 EGP"},
  {level:5,  threshold:88.67, name:"Now We're Talking",    badge:"Levels%20and%20Badges/300.png",  prize:"300 EGP"},
  {level:6,  threshold:87.58, name:"Certified Disciplined",badge:"Levels%20and%20Badges/500.png",  prize:"500 EGP"},
  {level:7,  threshold:86.50, name:"Shredding Season",     badge:"Levels%20and%20Badges/800.png",  prize:"800 EGP"},
  {level:8,  threshold:85.42, name:"OHHH It's Working",    badge:"Levels%20and%20Badges/1000.png", prize:"1000 EGP"},
  {level:9,  threshold:84.33, name:"Serious Business",     badge:"Levels%20and%20Badges/1500.png", prize:"1500 EGP"},
  {level:10, threshold:83.25, name:"Almost Aesthetic",     badge:"Levels%20and%20Badges/2000.png", prize:"2000 EGP"},
  {level:11, threshold:82.17, name:"Mashallah Status",     badge:"Levels%20and%20Badges/2500.png", prize:"2500 EGP"},
  {level:12, threshold:81.08, name:"Final Boss Energy",    badge:"Levels%20and%20Badges/3000.png", prize:"3000 EGP"},
  {level:13, threshold:80.00, name:"SHREDDED — 80 KG",     badge:"Levels%20and%20Badges/Special%20Gift.png", prize:"The Special Gift"}
];

const MEALS = [
  { id:"m1", name:"Breakfast", when:"After waking", slots:[
    { t:"Eggs", o:[
      {id:"eggs1", n:"Whole eggs, scrambled", m:"3 eggs · spray only", k:222, p:19, d:1, c:1, f:15}
    ]},
    { t:"Carbs", o:[
      {id:"oats1", n:"Oats, dry",            m:"40 g · cooked in the milk", k:152, p:6, d:1, c:27, f:3},
      {id:"rice1", n:"Basmati rice, dry",    m:"35 g", k:123, p:2, c:28, f:0},
      {id:"pot1",  n:"White potato, peeled", m:"155 g raw", k:119, p:3, c:27, f:0},
      {id:"spot1", n:"Sweet potato, peeled", m:"130 g raw", k:117, p:2, c:27, f:0},
      {id:"tost1", n:"Whole wheat toast",    m:"2 slices (60 g)", k:148, p:5, c:27, f:2}
    ]},
    { t:"Milk", o:[
      {id:"milk1", n:"Skim milk", m:"250 ml · this is the oats milk", k:85, p:9, d:1, c:13, f:0}
    ]}
  ]},
  { id:"m2", name:"Around Gym", when:"Before or after training · creatine 5 g with this meal on training days", slots:[
    { t:"Protein — 58 g", o:[
      {id:"chk2",  n:"Chicken breast, skinless", m:"250 g raw", k:275, p:58, d:1, c:0, f:4},
      {id:"shr2",  n:"Shrimp / prawns, peeled",  m:"290 g raw", k:246, p:58, c:0, f:2},
      {id:"tuna2", n:"Canned tuna in water",     m:"230 g drained", k:267, p:58, c:0, f:4},
      {id:"fish2", n:"White fish — cod / tilapia / seabass", m:"290 g raw", k:278, p:58, c:0, f:5},
      {id:"beef2", n:"Lean beef steak, 10% fat", m:"265 g raw · +123 kcal, occasional", k:398, p:58, c:0, f:18}
    ]},
    { t:"Carbs — 35 g", o:[
      {id:"mash2", n:"White potato, mashed", m:"200 g raw · spray, salt, pepper", k:154, p:4, d:1, c:35, f:0},
      {id:"spot2", n:"Sweet potato",         m:"170 g raw", k:153, p:3, c:35, f:0},
      {id:"rice2", n:"Basmati rice, dry",    m:"45 g", k:158, p:3, c:36, f:0},
      {id:"pas2",  n:"Pasta, dry",           m:"50 g", k:175, p:6, c:36, f:1}
    ]},
    { t:"Veg", o:[
      {id:"sal2", n:"Salad", m:"cucumber, tomato, lettuce · free", k:20, p:1, d:1, c:4, f:0}
    ]}
  ]},
  { id:"mp", name:"Pre-Workout", when:"60 min before training · rest days: anytime", slots:[
    { t:"Fast carbs", o:[
      {id:"ban",  n:"Banana, ripe",  m:"65 g", k:58, p:1, d:1, c:15, f:0},
      {id:"str",  n:"Strawberries",  m:"150 g", k:48, p:1, c:11, f:0},
      {id:"mel",  n:"Watermelon",    m:"150 g", k:45, p:1, c:10, f:0},
      {id:"app",  n:"Apple, diced",  m:"85 g", k:44, p:0, c:11, f:0},
      {id:"man",  n:"Mango, peeled", m:"75 g", k:45, p:1, c:10, f:0},
      {id:"blu",  n:"Blueberries",   m:"80 g", k:46, p:1, c:11, f:0},
      {id:"pea",  n:"Peach",         m:"120 g", k:47, p:1, c:11, f:0}
    ]}
  ]},
  { id:"m3", name:"Dinner", when:"Last meal before sleep · highest protein of the day", slots:[
    { t:"Protein — 73 g", o:[
      {id:"chk3",  n:"Chicken breast, skinless", m:"315 g raw", k:346, p:73, d:1, c:0, f:4},
      {id:"shr3",  n:"Shrimp / prawns, peeled",  m:"360 g raw", k:306, p:72, c:0, f:2},
      {id:"tuna3", n:"Canned tuna in water",     m:"290 g drained", k:336, p:73, c:0, f:5},
      {id:"fish3", n:"White fish — cod / tilapia / seabass", m:"365 g raw", k:350, p:73, c:0, f:6},
      {id:"beef3", n:"Lean beef steak, 10% fat", m:"330 g raw · +149 kcal, occasional", k:495, p:73, c:0, f:23}
    ]},
    { t:"Fats", o:[
      {id:"alm3", n:"Almonds", m:"20 g · weighed — holds the fat floor", k:116, p:4, d:1, c:4, f:10}
    ]},
    { t:"Veg", o:[
      {id:"veg3", n:"Free veg", m:"cucumber, zucchini, greens · free", k:20, p:1, d:1, c:4, f:0}
    ]},
    { t:"Whey", o:[
      {id:"nowhey", n:"No whey today", m:"standard day", k:0, p:0, d:1, c:0, f:0},
      {id:"whey",   n:"Whey — 1 scoop", m:"30 g · heaviest 2 training days only", k:120, p:24, c:3, f:1}
    ]}
  ]}
];

/* ---------------- helpers ---------------- */
const EN_DAY=["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
const EN_MON=["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const iso = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;

/* Last meal lands ~1–2 AM and sleep is ~2–3 AM, so the calendar day is the wrong
   unit. The "day" runs 06:00 → 06:00. Anything logged before 6 AM belongs to the
   day before. */
const DAY_START_HOUR = 0;
const dayKey = (d=new Date()) => iso(new Date(d.getTime() - DAY_START_HOUR*3600e3));

let todayISO = dayKey();
let cur = todayISO;

function shiftKey(k,n){ const d=new Date(k+"T12:00:00"); d.setDate(d.getDate()+n); return iso(d); }
function keysBack(n,offset=0){
  const out=[]; for(let i=offset;i<offset+n;i++) out.push(shiftKey(todayISO,-i)); return out;
}
function fmtDay(k){
  if(k===todayISO) return "Today";
  if(k===shiftKey(todayISO,-1)) return "Yesterday";
  const d=new Date(k+"T12:00:00");
  return `${EN_DAY[d.getDay()]} ${d.getDate()} ${EN_MON[d.getMonth()]}`;
}
/* The app can sit open across midnight. Re-check whose day it is. */
function refreshToday(){
  const t=dayKey();
  if(t===todayISO) return;
  const wasOnToday = (cur===todayISO);
  todayISO = t;
  if(wasOnToday) cur = t;
  renderAll();
}
function dayLog(create=false){
  if(S.log[cur]) return S.log[cur];
  if(create) return (S.log[cur]={});
  return {};
}
function findOpt(meal, id){
  for(const s of meal.slots){ const o = s.o.find(x=>x.id===id); if(o) return o; }
  return null;
}
function mealTotals(meal,k=cur){
  const sel = (S.log[k]&&S.log[k][meal.id]) || {};
  let plannedK=0,plannedP=0,plannedC=0,plannedF=0;
  Object.values(sel).forEach(id=>{ const o=findOpt(meal,id); if(o){plannedK+=o.k;plannedP+=o.p;plannedC+=o.c||0;plannedF+=o.f||0;} });
  const extras=getOffPlanEntries(S,k,meal.id), extra=offPlanTotals(extras);
  return {
    k:Math.round((plannedK+extra.calories)*10)/10,
    p:Math.round((plannedP+extra.protein)*10)/10,
    c:Math.round((plannedC+extra.carbs)*10)/10,
    f:Math.round((plannedF+extra.fat)*10)/10,
    plannedK,plannedP,plannedC,plannedF,
    extraK:extra.calories,extraP:extra.protein,extraC:extra.carbs,extraF:extra.fat,
    count:Object.keys(sel).length,extraCount:extras.length
  };
}
function dayTotals(){
  let k=0,p=0;
  MEALS.forEach(m=>{ const t=mealTotals(m); k+=t.k; p+=t.p; });
  return {k,p};
}
function weekdayFromKey(k){
  const jsDay = new Date(k+"T12:00:00").getDay();
  return (jsDay+6)%7; // Monday = 0
}
function dueMeasurements(k){
  const d=weekdayFromKey(k), due=[];
  due.push(MEASURE_INFO.w);            // weight: every day
  due.push(MEASURE_INFO.st);           // steps: every day
  if(d===0) due.push(MEASURE_INFO.cs); // waist: Mondays
  return due;
}
function hasMeasurement(type,k){
  const arr = type==="w" ? S.w : type==="cs" ? S.cs : S.st;
  return arr.some(e=>e.d===k);
}
function stepsFor(k){
  const e=S.st.find(x=>x.d===k);
  return e ? e.v : null;
}
function mealCompletionFor(k){
  const l=S.log[k] || {};
  let missing=0, total=0, completeMeals=0;
  MEALS.forEach(meal=>{
    const sel=l[meal.id] || {};
    let mealMissing=0;
    meal.slots.forEach((slot,i)=>{
      total++;
      const id=sel[i], ok=id && slot.o.some(o=>o.id===id);
      if(!ok) mealMissing++;
    });
    if(!mealMissing) completeMeals++;
    missing += mealMissing;
  });
  return {missing,total,completeMeals};
}
function completionState(k){
  const d=dayStats(k), meal=mealCompletionFor(k);
  const due=dueMeasurements(k), missingDue=due.filter(m=>!hasMeasurement(m.type,k));
  const st=stepsFor(k);
  const items=[
    {ok:meal.missing===0, text:meal.missing ? `Finish meal choices: ${meal.missing} left` : "All meal choices logged"},
    {ok:d.water>=WATER_GOAL, text:d.water>=WATER_GOAL ? `Water hit ${d.water} bottles` : `Water: ${d.water} / ${WATER_GOAL} bottles`},
    {ok:d.p>=TARGET_P, text:d.p>=TARGET_P ? `Protein hit ${d.p} g` : `Protein: ${d.p} / ${TARGET_P} g`},
    {ok:st!==null && st>=STEP_GOAL, text:st===null ? `Steps: not logged (goal ${STEP_GOAL.toLocaleString()})` : (st>=STEP_GOAL ? `Steps hit ${st.toLocaleString()}` : `Steps: ${st.toLocaleString()} / ${STEP_GOAL.toLocaleString()}`)}
  ];
  const dueNonSteps=due.filter(m=>m.type!=="st");
  const missingNonSteps=missingDue.filter(m=>m.type!=="st");
  items.push(dueNonSteps.length
    ? {ok:!missingNonSteps.length, text:missingNonSteps.length ? `Today's measurements: ${missingNonSteps.map(m=>m.name).join(", ")}` : "Today's measurements logged"}
    : {ok:true, text:"No measurements due today"});
  return {complete:items.every(x=>x.ok), items, due, missingDue, stats:d, meal};
}

function kgText(v){
  const n=Math.max(0, Math.round(v*100)/100);
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}
function bestWeightKg(){
  const vals=S.w.map(e=>Number(e.v)).filter(v=>Number.isFinite(v));
  return vals.length ? Math.min(...vals) : START_W;
}
function rankForWeight(weightKg){
  const w=Number.isFinite(weightKg) ? weightKg : START_W;
  return RANKS.reduce((best,r)=>w<=r.threshold ? r : best, RANKS[0]);
}
function currentRankState(){
  const hasWeight=S.w.some(e=>Number.isFinite(Number(e.v)));
  const best=bestWeightKg();
  const rank=rankForWeight(best);
  const next=RANKS.find(r=>r.level===rank.level+1) || null;
  let progress=100, remaining=0;
  if(next){
    const span=rank.threshold-next.threshold;
    progress=span>0 ? Math.max(0, Math.min(100, (rank.threshold-best)/span*100)) : 0;
    remaining=Math.max(0, best-next.threshold);
  }
  return {hasWeight,best,rank,next,progress,remaining};
}
function ensureRankRewardBaseline(){
  if(!S.rankRewards || typeof S.rankRewards!=="object") S.rankRewards={};
  if(Number.isFinite(Number(S.rankRewardStartLevel))) return;
  S.rankRewardStartLevel=currentRankState().rank.level;
  save();
}
function renderRankCard(){
  ensureRankRewardBaseline();
  const st=currentRankState(), base=Number(S.rankRewardStartLevel)||1;
  document.getElementById("rankBadgeImg").src=st.rank.badge;
  document.getElementById("rankBadgeImg").alt=st.rank.name;
  document.getElementById("rankName").textContent=st.rank.name;
  document.getElementById("rankLevel").textContent=`${st.rank.level} / ${RANKS.length}`;
  document.getElementById("rankPrize").textContent=`Prize: ${st.rank.prize}`;
  document.getElementById("rankProgress").style.width=st.progress+"%";
  document.getElementById("rankSub").textContent=st.hasWeight
    ? "Your rank locks on the best weight you've logged"
    : "Log your first weight to start the climb";
  document.getElementById("rankWeight").textContent=st.hasWeight
    ? `Best weight: ${kgText(st.best)} kg`
    : `Start: ${kgText(START_W)} kg`;
  document.getElementById("rankNext").textContent=st.next
    ? `${kgText(st.remaining)} kg to ${st.next.name}`
    : "Final level unlocked";
  const rewarded=!!S.rankRewards[st.rank.level];
  let msg;
  if(st.rank.level===RANKS.length && rewarded) msg="The Special Gift is unlocked and claimed once ✓";
  else if(rewarded) msg="This level's prize is claimed once ✓";
  else if(st.rank.level<=base) msg="New prizes start from the next level up.";
  else msg="Level unlocked. Go claim your prize.";
  document.getElementById("rankClaim").textContent=msg;
}
function renderRankInfo(){
  ensureRankRewardBaseline();
  const st=currentRankState(), base=Number(S.rankRewardStartLevel)||1;
  document.getElementById("rankInfoList").innerHTML=RANKS.map(r=>{
    const achieved=r.level<=st.rank.level;
    const rewarded=!!S.rankRewards[r.level];
    const current=r.level===st.rank.level;
    let status;
    if(rewarded) status="Claimed ✓";
    else if(r.level<=base) status=r.level===1 ? "Start" : "Counted";
    else if(achieved) status="Unlocked";
    else status=`${kgText(st.best-r.threshold)} kg to go`;
    return `<div class="rank-level${current?" current":""}${rewarded?" claimed":""}${achieved?"":" locked"}">
      <img src="${esc(r.badge)}" alt="">
      <span><b>${esc(r.name)}</b><small>at ${kgText(r.threshold)} kg · ${esc(r.prize)}</small></span>
      <span class="status">${esc(status)}</span>
    </div>`;
  }).join("");
}
let rankInfoPrevOverflow="", rankCelebratePrevOverflow="";
function openRankInfo(){
  renderRankInfo();
  const el=document.getElementById("rankInfo");
  rankInfoPrevOverflow=document.body.style.overflow;
  el.classList.add("on");
  document.body.style.overflow="hidden";
}
function closeRankInfo(){
  const el=document.getElementById("rankInfo");
  el.classList.remove("on");
  document.body.style.overflow=rankInfoPrevOverflow;
}
function showRankCelebration(rank){
  const el=document.getElementById("rankCelebrate");
  if(!el) return;
  document.getElementById("rankCelebrateImg").src=rank.badge;
  document.getElementById("rankCelebrateImg").alt=rank.name;
  document.getElementById("rankCelebrateTitle").textContent=rank.name;
  document.getElementById("rankCelebrateMsg").textContent=rank.level===RANKS.length
    ? "The Special Gift is unlocked. 80 kg. You actually did it."
    : `${rank.prize} unlocked. You earned it — cash it.`;
  rankCelebratePrevOverflow=document.body.style.overflow;
  el.classList.add("on");
  document.body.style.overflow="hidden";
}
function closeRankCelebration(){
  const el=document.getElementById("rankCelebrate");
  if(!el) return;
  el.classList.remove("on");
  document.body.style.overflow=rankCelebratePrevOverflow;
}
function maybeCelebrateRank(){
  if(!Number.isFinite(Number(S.rankRewardStartLevel))){
    ensureRankRewardBaseline();
    return false;
  }
  if(!S.rankRewards || typeof S.rankRewards!=="object") S.rankRewards={};
  const st=currentRankState(), base=Number(S.rankRewardStartLevel)||1;
  const unlocked=RANKS.filter(r=>r.level>base && r.level<=st.rank.level && !S.rankRewards[r.level]);
  if(!unlocked.length) return false;
  unlocked.forEach(r=>{ S.rankRewards[r.level]=true; });
  save();
  renderRankCard();
  if(document.getElementById("rankInfo").classList.contains("on")) renderRankInfo();
  showRankCelebration(unlocked[unlocked.length-1]);
  return true;
}
let toastT;
function toast(msg){
  const el=document.getElementById("toast");
  el.textContent=msg; el.classList.add("on");
  clearTimeout(toastT); toastT=setTimeout(()=>el.classList.remove("on"),1700);
}
let celebratePrevOverflow="";
function showCelebration(){
  const el=document.getElementById("celebrate");
  if(!el) return;
  celebratePrevOverflow=document.body.style.overflow;
  el.classList.add("on");
  document.body.style.overflow="hidden";
}
function closeCelebration(){
  const el=document.getElementById("celebrate");
  if(!el) return;
  el.classList.remove("on");
  document.body.style.overflow=celebratePrevOverflow;
}
function maybeCelebrate(){
  const st=completionState(cur);
  if(!st.complete || S.rewarded[cur]) return;
  S.rewarded[cur]=true;
  save();
  renderReward();
  showCelebration();
}

/* ---------------- render: today ---------------- */
function renderReward(){
  const st=completionState(cur);
  const card=document.getElementById("rewardCard");
  const badge=document.getElementById("rewardBadge");
  const sub=document.getElementById("rewardSub");
  const list=document.getElementById("rewardList");
  const rem=document.getElementById("measureReminder");
  const done=st.items.filter(x=>x.ok).length;
  card.classList.toggle("done", st.complete);
  badge.className="badge"+(st.complete?"":" empty");
  badge.textContent = st.complete ? "Complete" : `${done} / ${st.items.length}`;
  sub.textContent = st.complete
    ? "Day complete. That's the standard."
    : "Complete everything to unlock the day.";
  list.innerHTML = st.items.map(x=>`<div class="reward-item ${x.ok?"ok":"todo"}"><i>${x.ok?"✓":"!"}</i><span>${x.text}</span></div>`).join("");
  const missingNonSteps=st.missingDue.filter(m=>m.type!=="st");
  rem.innerHTML = missingNonSteps.length
    ? `<div class="note reward-reminder"><b>Today's measurements</b>Log ${missingNonSteps.map(m=>m.name).join(" and ")} from the Measures tab.</div>`
    : "";
}
function renderHeader(){
  const h=new Date().getHours();
  const note = (cur===todayISO && h<DAY_START_HOUR) ? '<span class="dnote">Day closes at 6 AM</span>' : "";
  document.getElementById("dlabel").innerHTML = fmtDay(cur)+note;
  document.getElementById("next").disabled = (cur===todayISO);
  const {k,p}=dayTotals();
  document.getElementById("kcal").textContent=k;
  document.getElementById("prot").textContent=p;
  const kb=document.getElementById("kbar"), pb=document.getElementById("pbar");
  kb.firstElementChild.style.width=Math.min(100,k/TARGET_KCAL*100)+"%";
  pb.firstElementChild.style.width=Math.min(100,p/TARGET_P*100)+"%";
  kb.classList.toggle("over", k>TARGET_KCAL+40);
}
function renderMeals(){
  const wrap=document.getElementById("meals"); wrap.innerHTML="";
  MEALS.forEach((meal,mi)=>{
    const t=mealTotals(meal);
    const sel=(S.log[cur]&&S.log[cur][meal.id])||{};
    const extras=getOffPlanEntries(S,cur,meal.id);
    const card=document.createElement("div"); card.className="card";
    const plannedLines = Object.values(sel).map(id=>{
      const o=findOpt(meal,id); if(!o) return "";
      return `<div><span>${o.n}</span><span>${o.m||""}</span></div>`;
    }).join("");
    const extraLines=extras.map(entry=>`<div><span>↳ ${esc(entry.name)} <small>off-plan</small></span><span>${entry.grams} g · ${Math.round(entry.macros.calories)} kcal</span></div>`).join("");
    const hasFood=t.count||t.extraCount;
    card.innerHTML=`
      <div class="chead">
        <div><h2>${["①","②","③","④"][mi]} ${meal.name}</h2><p class="sub">${meal.when}</p></div>
        <div class="badge ${hasFood?"":"empty"}">${hasFood?`<span class="num">${Math.round(t.k)}</span> kcal · <span class="num">${Math.round(t.p)}</span> g P`:"Not yet"}</div>
      </div>
      ${hasFood?`<div class="lines">${plannedLines}${extraLines}</div>`:`<p class="empty-note">Nothing logged yet.</p>`}
      ${(S.notes[cur]&&S.notes[cur][meal.id])?`<div class="meal-note-show">✎ ${esc(S.notes[cur][meal.id])}</div>`:""}
      <div class="acts">
        ${t.count
          ? `<button class="btn ghost" data-clear="${meal.id}">Clear planned</button><button class="btn pri" data-edit="${meal.id}">Edit / add food</button>`
          : `<button class="btn sec" data-edit="${meal.id}">${extras.length?"Add planned choices":"Choose / add food"}</button><button class="btn pri" data-quick="${meal.id}">Log full meal</button>`}
      </div>`;
    wrap.appendChild(card);
  });
  wrap.querySelectorAll("[data-quick]").forEach(b=>b.onclick=()=>quickLog(b.dataset.quick));
  wrap.querySelectorAll("[data-edit]").forEach(b=>b.onclick=()=>openSheet(b.dataset.edit));
  wrap.querySelectorAll("[data-clear]").forEach(b=>b.onclick=()=>{
    const log=dayLog();
    delete log[b.dataset.clear];
    if(!Object.keys(log).length) delete S.log[cur];
    save(); renderAll(); toast("Planned choices cleared");
  });
}
function quickLog(id){
  const meal=MEALS.find(m=>m.id===id);
  const sel={};
  meal.slots.forEach((s,i)=>{ const d=s.o.find(o=>o.d); if(d) sel[i]=d.id; });
  dayLog(true)[id]=sel; save(); renderAll(); maybeCelebrate(); toast(meal.name+" logged ✓");
}
function renderWater(){
  const n=dayLog().water||0;
  const dots=document.getElementById("wdots"); dots.innerHTML="";
  for(let i=0;i<WATER_MAX;i++){
    const b=document.createElement("button");
    b.className="dot"+(i<n?" on":"")+(i>=WATER_GOAL?" extra":"");
    b.setAttribute("aria-label",`Bottle ${i+1}`+(i>=WATER_GOAL?" (extra)":""));
    b.onclick=()=>{ dayLog(true).water=(i<n)?i:i+1; save(); renderAll(); maybeCelebrate(); };
    dots.appendChild(b);
  }
  const badge=document.getElementById("wbadge");
  const litres=(n*BOTTLE_L).toFixed(1);
  badge.textContent = n>=WATER_GOAL ? `${litres} L ✓` : `${litres} L`;
  badge.classList.toggle("empty", n===0);
}
document.getElementById("wplus").onclick=()=>{ const l=dayLog(true); l.water=Math.min(WATER_MAX,(l.water||0)+1); save(); renderAll(); maybeCelebrate(); };
document.getElementById("wminus").onclick=()=>{ const l=dayLog(true); l.water=Math.max(0,(l.water||0)-1); save(); renderAll(); maybeCelebrate(); };

/* ---------------- sheet ---------------- */
let sheetMeal=null;
function openSheet(id){
  sheetMeal=MEALS.find(m=>m.id===id);
  document.getElementById("sTitle").textContent=sheetMeal.name;
  renderSheet();
  document.getElementById("scrim").classList.add("on");
  document.getElementById("sheet").classList.add("on");
  document.body.style.overflow="hidden";
}
function closeSheet(){
  document.getElementById("scrim").classList.remove("on");
  document.getElementById("sheet").classList.remove("on");
  document.body.style.overflow=""; sheetMeal=null; renderAll();
}
function renderSheet(){
  const sel=(S.log[cur]&&S.log[cur][sheetMeal.id])||{};
  const body=document.getElementById("sBody"); body.innerHTML="";
  sheetMeal.slots.forEach((slot,i)=>{
    const box=document.createElement("div"); box.className="slot";
    box.innerHTML=`<h3>${slot.t}${slot.o.length>1?" — pick one":""}</h3>`;
    slot.o.forEach(o=>{
      const b=document.createElement("button");
      b.className="opt"+(sel[i]===o.id?" on":"");
      b.innerHTML=`<span class="tick">✓</span>
        <span class="t"><b>${o.n}${o.d?' <span class="star">★</span>':""}</b>${o.m?`<small>${o.m}</small>`:""}</span>
        <span class="kc">${o.k} kcal</span>`;
      b.onclick=()=>{
        const l=dayLog(true); l[sheetMeal.id]=l[sheetMeal.id]||{};
        if(l[sheetMeal.id][i]===o.id) delete l[sheetMeal.id][i];
        else l[sheetMeal.id][i]=o.id;
        if(!Object.keys(l[sheetMeal.id]).length) delete l[sheetMeal.id];
        save(); renderSheet(); renderHeader(); renderReward(); maybeCelebrate();
      };
      box.appendChild(b);
    });
    body.appendChild(box);
  });
  renderOffPlanSection(body);
  const nv=(S.notes[cur]&&S.notes[cur][sheetMeal.id])||"";
  if(nv){
    const legacy=document.createElement("div"); legacy.className="legacy-note";
    const label=document.createElement("b"); label.textContent="Legacy note";
    const text=document.createElement("div"); text.textContent=nv;
    legacy.append(label,text); body.appendChild(legacy);
  }
  const t=mealTotals(sheetMeal);
  document.getElementById("sTot").innerHTML=`<span class="num">${Math.round(t.k)}</span> kcal · <span class="num">${Math.round(t.p)}</span> g protein`;
}

function renderOffPlanSection(body){
  const box=document.createElement("div"); box.className="slot offplan-section";
  const head=document.createElement("div"); head.className="offplan-head";
  const copy=document.createElement("div");
  const title=document.createElement("h3"); title.textContent="Off-plan food";
  const sub=document.createElement("p"); sub.textContent="Log anything extra without changing the plan choices above.";
  copy.append(title,sub);
  const add=document.createElement("button"); add.className="btn pri"; add.type="button"; add.textContent="Add food";
  head.append(copy,add); box.appendChild(head);

  const entries=getOffPlanEntries(S,cur,sheetMeal.id);
  if(entries.length){
    const list=document.createElement("div"); list.className="offplan-list";
    entries.forEach(entry=>{
      const row=document.createElement("div"); row.className="offplan-row";
      const info=document.createElement("div");
      const name=document.createElement("b"); name.textContent=entry.name;
      const desc=document.createElement("small"); desc.textContent=`${entry.grams} g · ${entry.provider}${entry.description?` · ${entry.description}`:""}`;
      const macros=document.createElement("div"); macros.className="offplan-macros";
      macros.textContent=`${Math.round(entry.macros.calories)} kcal · P ${entry.macros.protein} · C ${entry.macros.carbs} · F ${entry.macros.fat}`;
      info.append(name,desc,macros);
      const actions=document.createElement("div"); actions.className="offplan-actions";
      const edit=document.createElement("button"); edit.className="btn sec"; edit.type="button"; edit.textContent="Edit";
      edit.onclick=()=>openFoodSearch(box,entry);
      const remove=document.createElement("button"); remove.className="btn ghost"; remove.type="button"; remove.textContent="Remove";
      remove.onclick=()=>removeOffPlanFood(entry.id);
      actions.append(edit,remove); row.append(info,actions); list.appendChild(row);
    });
    box.appendChild(list);
  }
  add.onclick=()=>openFoodSearch(box);
  body.appendChild(box);
}

function openFoodSearch(container,existing=null){
  container.querySelector(".food-search-panel")?.remove();
  const panel=document.createElement("div"); panel.className="food-search-panel";
  container.appendChild(panel);
  if(existing){
    openFoodAmountEditor(panel,foodFromLoggedEntry(existing),existing);
    return;
  }
  const input=document.createElement("input");
  input.type="search"; input.placeholder="Search chicken, rice, bread…"; input.autocomplete="off"; input.maxLength=80;
  input.setAttribute("aria-label","Search food catalog");
  const status=document.createElement("div"); status.className="food-search-status"; status.textContent="Search the offline USDA catalog first.";
  const results=document.createElement("div"); results.className="food-results";
  const online=document.createElement("button"); online.className="btn ghost"; online.type="button"; online.textContent="Search online branded foods"; online.hidden=true;
  const custom=document.createElement("button"); custom.className="btn sec"; custom.type="button"; custom.textContent="Create custom food from a label";
  custom.onclick=()=>renderCustomFoodForm(panel);
  panel.append(input,status,results,online,custom);
  let timer;
  let searchGeneration=0;
  input.oninput=()=>{
    clearTimeout(timer);
    const generation=++searchGeneration;
    online.hidden=true;
    online.onclick=null;
    timer=setTimeout(async()=>{
      const query=input.value.trim(); results.innerHTML="";
      if(query.length<2){ status.textContent="Type at least 2 characters."; return; }
      status.textContent="Searching offline foods…";
      try{
        const foods=await searchCatalog(query,S.customFoods,30);
        if(generation!==searchGeneration) return;
        status.textContent=foods.length?`${foods.length} offline result${foods.length===1?"":"s"}`:"No offline match.";
        renderFoodResults(results,foods,panel);
        online.hidden=false;
        online.onclick=async()=>{
          online.disabled=true; status.textContent="Searching online brands…";
          try{
            const rows=await searchOnlineBrandedFoods(query);
            if(generation!==searchGeneration) return;
            status.textContent=rows.length?`${rows.length} online result${rows.length===1?"":"s"}`:"No online branded match.";
            renderFoodResults(results,rows,panel);
          }catch(error){ if(generation===searchGeneration) status.textContent=error.message||"Online search unavailable."; }
          finally{online.disabled=false;}
        };
      }catch(error){ if(generation===searchGeneration) status.textContent=error.message||"Food search failed."; }
    },180);
  };
  input.focus();
}

function renderFoodResults(results,foods,panel){
  results.innerHTML="";
  foods.forEach(food=>{
    const button=document.createElement("button"); button.type="button"; button.className="food-result";
    const provider=document.createElement("span"); provider.className="provider"; provider.textContent=`${food.provider} · ${food.dataType||"Food"}`;
    const name=document.createElement("b"); name.textContent=food.name;
    const desc=document.createElement("small"); desc.textContent=food.description||food.preparation||"Per 100 g";
    const macros=document.createElement("div"); macros.className="offplan-macros";
    macros.textContent=`per 100 g · ${Math.round(food.per100g.calories)} kcal · P ${food.per100g.protein} · C ${food.per100g.carbs} · F ${food.per100g.fat}`;
    button.append(provider,name,desc,macros);
    button.onclick=()=>{
      if(food.dataType==="Branded" && !S.customFoods.some(item=>item.id===food.id)){
        S.customFoods.push(food); save();
      }
      openFoodAmountEditor(panel,food);
    };
    results.appendChild(button);
  });
}

function openFoodAmountEditor(panel,food,existing=null){
  panel.innerHTML="";
  const card=document.createElement("div"); card.className="food-amount";
  const name=document.createElement("b"); name.textContent=food.name;
  const source=document.createElement("small"); source.textContent=`${food.provider} · ${food.description||food.dataType||"Food"}`;
  const grid=document.createElement("div"); grid.className="food-amount-grid";
  const amount=document.createElement("input"); amount.type="number"; amount.inputMode="decimal"; amount.min="0.1"; amount.max="5000"; amount.step="0.1";
  amount.value=String(existing?.grams||food.portions?.[0]?.grams||100); amount.setAttribute("aria-label","Amount in grams");
  const portion=document.createElement("select"); portion.setAttribute("aria-label","Common serving");
  const customOption=document.createElement("option"); customOption.value=""; customOption.textContent="Custom grams"; portion.appendChild(customOption);
  (food.portions||[]).forEach(item=>{ const option=document.createElement("option"); option.value=String(item.grams); option.textContent=`${item.label} (${item.grams} g)`; portion.appendChild(option); });
  portion.onchange=()=>{ if(portion.value) amount.value=portion.value; update(); };
  const preview=document.createElement("div"); preview.className="offplan-macros";
  const actions=document.createElement("div"); actions.className="acts";
  const back=document.createElement("button"); back.className="btn sec"; back.type="button"; back.textContent="Back"; back.onclick=()=>openFoodSearch(panel.parentElement);
  const add=document.createElement("button"); add.className="btn pri"; add.type="button"; add.textContent=existing?"Save amount":"Add to meal";
  const update=()=>{
    const grams=Number(amount.value);
    if(!(grams>0)){preview.textContent="Enter an amount greater than zero.";return;}
    const factor=grams/100;
    preview.textContent=`${Math.round(food.per100g.calories*factor)} kcal · P ${(food.per100g.protein*factor).toFixed(1)} · C ${(food.per100g.carbs*factor).toFixed(1)} · F ${(food.per100g.fat*factor).toFixed(1)}`;
  };
  amount.oninput=update;
  add.onclick=()=>{
    try{
      const entry=createLoggedFoodEntry(food,cur,sheetMeal.id,Number(amount.value),existing?.id);
      if(existing) entry.createdAt=existing.createdAt;
      upsertOffPlanFood(entry);
    }catch(error){toast(error.message||"Check the food amount");}
  };
  grid.append(amount,portion); actions.append(back,add); card.append(name,source,grid,preview,actions); panel.appendChild(card); update();
}

function renderCustomFoodForm(panel){
  panel.innerHTML=`<div class="food-amount"><b>Create food from its nutrition label</b><small>Enter values for one labelled serving.</small><div class="custom-food-grid"><input class="wide" data-custom="name" maxlength="120" placeholder="Food name"><input data-custom="grams" type="number" inputmode="decimal" min="0.1" placeholder="Serving grams"><input data-custom="calories" type="number" inputmode="decimal" min="0" placeholder="Calories"><input data-custom="protein" type="number" inputmode="decimal" min="0" placeholder="Protein g"><input data-custom="carbs" type="number" inputmode="decimal" min="0" placeholder="Carbs g"><input data-custom="fat" type="number" inputmode="decimal" min="0" placeholder="Fat g"></div><div class="acts"><button class="btn sec" data-custom-back type="button">Back</button><button class="btn pri" data-custom-save type="button">Save & add</button></div></div>`;
  panel.querySelector("[data-custom-back]").onclick=()=>openFoodSearch(panel.parentElement);
  panel.querySelector("[data-custom-save]").onclick=()=>{
    try{
      const value=name=>panel.querySelector(`[data-custom="${name}"]`).value;
      const food=createCustomFood({name:value("name"),servingGrams:Number(value("grams")),calories:Number(value("calories")),protein:Number(value("protein")),carbs:Number(value("carbs")),fat:Number(value("fat"))});
      S.customFoods.push(food); save(); openFoodAmountEditor(panel,food);
    }catch(error){toast(error.message||"Check the custom food values");}
  };
  panel.querySelector('[data-custom="name"]').focus();
}

function foodFromLoggedEntry(entry){
  const factor=100/entry.grams;
  const searchText=normalizeSearchText(`${entry.name} ${entry.description||""}`);
  return {id:entry.foodId,provider:entry.provider,providerId:entry.providerId,catalogVersion:entry.sourceVersion,name:entry.name,description:entry.description,dataType:"Logged food snapshot",per100g:{calories:entry.macros.calories*factor,protein:entry.macros.protein*factor,carbs:entry.macros.carbs*factor,fat:entry.macros.fat*factor},portions:[],searchText,tokens:searchText.split(/\s+/)};
}

function upsertOffPlanFood(entry){
  S.offPlan[cur]=S.offPlan[cur]||{};
  const rows=S.offPlan[cur][sheetMeal.id]=S.offPlan[cur][sheetMeal.id]||[];
  const index=rows.findIndex(item=>item.id===entry.id);
  if(index>=0) rows[index]=entry; else rows.push(entry);
  save(); renderSheet(); renderHeader(); renderReward(); renderMeals(); renderAnalytics(); maybeCelebrate(); toast(index>=0?"Food updated":"Off-plan food added");
}

function removeOffPlanFood(id){
  const rows=S.offPlan?.[cur]?.[sheetMeal.id]||[];
  S.offPlan[cur][sheetMeal.id]=rows.filter(entry=>entry.id!==id);
  if(!S.offPlan[cur][sheetMeal.id].length) delete S.offPlan[cur][sheetMeal.id];
  if(!Object.keys(S.offPlan[cur]).length) delete S.offPlan[cur];
  save(); renderSheet(); renderHeader(); renderReward(); renderMeals(); renderAnalytics(); toast("Off-plan food removed");
}
document.getElementById("sDone").onclick=closeSheet;
document.getElementById("scrim").onclick=closeSheet;
document.getElementById("sDefault").onclick=()=>{ quickLog(sheetMeal.id); renderSheet(); };

/* ---------------- measures ---------------- */
function pushEntry(arr,val){
  const i=arr.findIndex(e=>e.d===cur);
  if(i>=0) arr[i]=Object.assign(arr[i],val); else arr.push(Object.assign({d:cur},val));
  arr.sort((a,b)=>a.d<b.d?-1:1); save();
}
function last(arr){ return arr.length?arr[arr.length-1]:null; }
function avgWindow(arr,days,endOffset=0){
  const ks=new Set(keysBack(days,endOffset));
  const vs=arr.filter(e=>ks.has(e.d)).map(e=>e.v);
  return vs.length ? vs.reduce((a,b)=>a+b,0)/vs.length : null;
}
function histHTML(arr,fmt,n=7){
  if(!arr.length) return `<div style="border:none"><span>Nothing logged yet</span></div>`;
  return [...arr].sort((a,b)=>a.d<b.d?1:(a.d>b.d?-1:0)).slice(0,n).map(e=>`<div><span>${fmtDay(e.d)}</span><b>${fmt(e)}</b></div>`).join("");
}
function spark(arr){
  const el=document.getElementById("wSpark");
  const pts=[...arr].sort((a,b)=>a.d<b.d?-1:(a.d>b.d?1:0)).slice(-12);
  if(pts.length<2){ el.innerHTML=""; return; }
  const vs=pts.map(p=>p.v), mn=Math.min(...vs), mx=Math.max(...vs), rng=(mx-mn)||1;
  const W=300,H=64,pad=6;
  const xy=pts.map((p,i)=>[ (i/(pts.length-1))*W, pad + (1-(p.v-mn)/rng)*(H-pad*2) ]);
  const d=xy.map((c,i)=>(i?"L":"M")+c[0].toFixed(1)+","+c[1].toFixed(1)).join(" ");
  el.innerHTML=`<path d="${d}" fill="none" stroke="#16160F" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    ${xy.map(c=>`<circle cx="${c[0].toFixed(1)}" cy="${c[1].toFixed(1)}" r="2.6" fill="#16160F"/>`).join("")}`;
}
function renderMeasureDue(){
  const due=dueMeasurements(cur);
  Object.values(MEASURE_INFO).forEach(m=>{
    const isDue=due.some(x=>x.type===m.type);
    const done=isDue && hasMeasurement(m.type,cur);
    const card=document.getElementById(m.card);
    const badge=document.getElementById(m.badge);
    if(card){
      card.classList.toggle("due", isDue && !done);
      card.classList.toggle("done-today", isDue && done);
    }
    if(badge){
      badge.hidden=!isDue;
      badge.className="badge due-badge"+(done?"":" empty");
      badge.textContent=done ? "Logged ✓" : "Due today";
    }
  });
}
function renderMeas(){
  // weight
  const a7=avgWindow(S.w,7), a7prev=avgWindow(S.w,7,7);
  document.getElementById("wAvg").textContent = a7? a7.toFixed(1) : (last(S.w)? last(S.w).v.toFixed(1) : "—");
  const wd=document.getElementById("wDelta");
  if(a7&&a7prev){
    const diff=a7-a7prev;
    wd.textContent=(diff<=0?"▼ ":"▲ ")+Math.abs(diff).toFixed(2)+" kg vs last week";
    wd.className="delta "+(diff<=0?"down":"up");
  } else if(a7){
    const diff=a7-START_W;
    wd.textContent=(diff<=0?"▼ ":"▲ ")+Math.abs(diff).toFixed(1)+" kg from start (93)";
    wd.className="delta "+(diff<=0?"down":"up");
  } else { wd.textContent="7-day average"; wd.className="delta"; wd.style.color="var(--soft)"; }
  spark(S.w);
  document.getElementById("wHist").innerHTML=histHTML(S.w,e=>e.v.toFixed(1)+" kg");

  // steps
  const sToday=stepsFor(cur), sl=last(S.st), sAvg=avgWindow(S.st,7);
  document.getElementById("stLast").innerHTML =
    sToday!==null ? `<span class="num">${sToday.toLocaleString()}</span>`
    : (sl ? `<span class="num">${sl.v.toLocaleString()}</span>` : "—");
  const sd=document.getElementById("stDelta");
  if(sAvg!==null){
    const ok=sAvg>=STEP_GOAL;
    sd.textContent=`7-day avg: ${Math.round(sAvg).toLocaleString()} / ${STEP_GOAL.toLocaleString()}`;
    sd.className="delta "+(ok?"down":"up");
  } else { sd.textContent="7-day average"; sd.className="delta"; sd.style.color="var(--soft)"; }
  document.getElementById("stHist").innerHTML=histHTML(S.st,e=>e.v.toLocaleString()+" steps",7);

  // waist — baseline is the first logged entry
  const cl=last(S.cs), cFirst=S.cs.length?S.cs[0]:null;
  document.getElementById("csLast").textContent = cl? cl.v : "—";
  const cd=document.getElementById("csDelta");
  if(cl&&cFirst&&S.cs.length>1){
    const diff=cl.v-cFirst.v;
    cd.textContent=(diff<=0?"▼ ":"▲ ")+Math.abs(diff).toFixed(1)+` cm from start (${cFirst.v})`;
    cd.className="delta "+(diff<=0?"down":"up");
  } else if(cl){
    cd.textContent="Baseline set — remeasure next Monday"; cd.className="delta"; cd.style.color="var(--soft)";
  } else {
    cd.textContent="Log your first measurement"; cd.className="delta"; cd.style.color="var(--soft)";
  }
  document.getElementById("csHist").innerHTML=histHTML(S.cs,e=>e.v+" cm");
  renderMeasureDue();
}
document.getElementById("wAdd").onclick=()=>{
  const el=document.getElementById("wIn"), v=parseFloat(el.value);
  if(!(v>40&&v<200)) return toast("Enter a real number");
  pushEntry(S.w,{v:Math.round(v*10)/10}); el.value=""; el.blur(); renderAll(); maybeCelebrate(); maybeCelebrateRank(); toast("Logged ✓");
};
document.getElementById("stAdd").onclick=()=>{
  const el=document.getElementById("stIn"), v=parseInt(el.value);
  if(!(v>=0&&v<60000)) return toast("Enter a real number");
  pushEntry(S.st,{v}); el.value=""; el.blur(); renderAll(); maybeCelebrate(); toast("Logged ✓");
};
document.getElementById("csAdd").onclick=()=>{
  const el=document.getElementById("csIn"), v=parseFloat(el.value);
  if(!(v>40&&v<220)) return toast("Enter a real number");
  pushEntry(S.cs,{v:Math.round(v*2)/2}); el.value=""; el.blur(); renderAll(); maybeCelebrate(); toast("Logged ✓");
};
document.getElementById("reset").onclick=async()=>{
  if(!confirm("This erases every log. Sure?")) return;
  await stateStore.clear();
  S=normalizeAppState({});
  await stateStore.save(S);
  renderAll(); toast("All data erased");
};

/* ---------------- report ---------------- */
function dayStats(k){
  const l = S.log[k] || {};
  let plannedK=0, plannedP=0, plannedC=0, plannedF=0; const names=[];
  MEALS.forEach(m=>{
    Object.values(l[m.id]||{}).forEach(id=>{
      const o=findOpt(m,id); if(o){ plannedK+=o.k; plannedP+=o.p; plannedC+=o.c||0; plannedF+=o.f||0; names.push(o.n); }
    });
  });
  const extras=getOffPlanEntries(S,k), extra=offPlanTotals(extras);
  names.push(...extras.map(entry=>entry.name));
  const mealsLogged = MEALS.filter(m=>(l[m.id] && Object.keys(l[m.id]).length)||getOffPlanEntries(S,k,m.id).length).length;
  return {
    kc:Math.round((plannedK+extra.calories)*10)/10,
    p:Math.round((plannedP+extra.protein)*10)/10,
    cb:Math.round((plannedC+extra.carbs)*10)/10,
    ft:Math.round((plannedF+extra.fat)*10)/10,
    plannedK,plannedP,plannedC,plannedF,
    extraK:extra.calories,extraP:extra.protein,extraC:extra.carbs,extraF:extra.fat,
    names,extras,mealsLogged,water:l.water||0
  };
}
function allKeys(){
  const s=new Set(Object.keys(S.log));
  S.w.forEach(e=>s.add(e.d)); S.cs.forEach(e=>s.add(e.d)); S.st.forEach(e=>s.add(e.d));
  Object.keys(S.notes||{}).forEach(k=>s.add(k));
  Object.keys(S.offPlan||{}).forEach(k=>s.add(k));
  return [...s].sort();
}
function csvCell(v){ return safeCsvCell(v); }
function dayNotes(k){ const nn=S.notes&&S.notes[k]; if(!nn) return ""; return MEALS.map(m=>nn[m.id]?`${m.name}: ${nn[m.id]}`:"").filter(Boolean).join(" | "); }
function offPlanFoodNames(k){ return getOffPlanEntries(S,k).map(entry=>`${entry.name} (${entry.grams} g)`).join(" | "); }
function mealSlotStatus(k, meal){
  const sel = (S.log[k] && S.log[k][meal.id]) || {};
  const selected=[], missing=[];
  meal.slots.forEach((slot,i)=>{
    const id=sel[i], o=id && slot.o.find(x=>x.id===id);
    if(o) selected.push({meal, slot, slotIndex:i, option:o});
    else missing.push(slot);
  });
  return {selected, missing, complete:missing.length===0, skipped:selected.length===0};
}
function selectedOptionsForDay(k){
  return MEALS.flatMap(meal=>mealSlotStatus(k,meal).selected);
}
function isDietDay(k){
  const l=S.log[k] || {};
  return (l.water||0)>0 || MEALS.some(m=>l[m.id] && Object.keys(l[m.id]).length) || getOffPlanEntries(S,k).length>0;
}
function missingMealParts(k){
  if(!isDietDay(k)) return "";
  const parts=[];
  MEALS.forEach(meal=>{
    const st=mealSlotStatus(k, meal);
    if(!st.missing.length) return;
    parts.push(st.skipped
      ? `${meal.name}: whole meal`
      : `${meal.name}: ${st.missing.map(s=>s.t).join(", ")}`);
  });
  return parts.join(" | ");
}
function toCSV(){
  const wm={}, cm={}, sm={};
  S.w.forEach(e=>wm[e.d]=e.v); S.cs.forEach(e=>cm[e.d]=e.v); S.st.forEach(e=>sm[e.d]=e.v);
  const rows=[["date","kcal","protein_g","carbs_g","fat_g","planned_kcal","off_plan_kcal","off_plan_protein_g","off_plan_carbs_g","off_plan_fat_g","water_L","steps","weight_kg",
    "waist_cm","meals_logged","off_plan_foods","notes","missing_meal_parts"].join(",")];
  allKeys().forEach(k=>{
    const d=dayStats(k);
    rows.push([k,d.kc,d.p,d.cb,d.ft,d.plannedK,d.extraK,d.extraP,d.extraC,d.extraF,(d.water*BOTTLE_L).toFixed(1),
      sm[k]!==undefined?sm[k]:"",
      wm[k]!==undefined?wm[k]:"", cm[k]!==undefined?cm[k]:"",
      d.mealsLogged, csvCell(offPlanFoodNames(k)), csvCell(dayNotes(k)), csvCell(missingMealParts(k))].join(","));
  });
  return rows.join("\r\n");
}
function toFoodEntriesCSV(){
  const rows=[["date","meal","entry_id","provider","provider_food_id","source_version","food","description","grams","kcal","protein_g","carbs_g","fat_g"].join(",")];
  Object.keys(S.offPlan||{}).sort().forEach(k=>{
    MEALS.forEach(meal=>getOffPlanEntries(S,k,meal.id).forEach(entry=>{
      rows.push([k,csvCell(meal.name),csvCell(entry.id),entry.provider,csvCell(entry.providerId),csvCell(entry.sourceVersion),csvCell(entry.name),csvCell(entry.description||""),entry.grams,entry.macros.calories,entry.macros.protein,entry.macros.carbs,entry.macros.fat].join(","));
    }));
  });
  return rows.join("\r\n");
}
function esc(v){
  return String(v).replace(/[&<>"']/g,c=>({"&":"&amp;","<":"&lt;",">":"&gt;","\"":"&quot;","'":"&#39;"}[c]));
}
function pct(count,total){ return total ? Math.round(count/total*100) : 0; }
function inc(map,key,meta){
  let row=map.get(key);
  if(!row){ row=Object.assign({count:0},meta); map.set(key,row); }
  row.count++;
  return row;
}
function ranked(map){
  return [...map.values()].sort((a,b)=>b.count-a.count || String(a.item||a.meal).localeCompare(String(b.item||b.meal),"en"));
}
function longestStreak(keys,pred){
  let best=0, curN=0, prev=null;
  keys.forEach(k=>{
    if(!pred(k)){ curN=0; prev=k; return; }
    curN = (prev && shiftKey(prev,1)===k) ? curN+1 : 1;
    best=Math.max(best,curN); prev=k;
  });
  return best;
}
function buildAnalytics(){
  const keys=allKeys().filter(isDietDay);
  const total=keys.length;
  const empty={keys,totalDays:0,metrics:[],topFoods:[],slotChoices:[],slotWinners:[],mealConsistency:[],mealSkipped:[],missingRows:[],streaks:[]};
  if(!total) return empty;
  const optionMap=new Map(), slotMap=new Map(), missingMap=new Map();
  const mealStats=MEALS.map(m=>({meal:m.name,count:0,complete:0,skipped:0,partial:0,total}));
  const dayRows=keys.map(k=>({k,stats:dayStats(k),reward:completionState(k).complete,meal:mealCompletionFor(k)}));
  keys.forEach(k=>{
    MEALS.forEach((meal,mi)=>{
      const st=mealSlotStatus(k,meal), ms=mealStats[mi];
      if(st.complete) ms.complete++;
      else if(st.skipped) ms.skipped++;
      else ms.partial++;
      st.selected.forEach(x=>{
        inc(optionMap,x.option.id,{meal:meal.name,slot:x.slot.t,item:x.option.n,note:x.option.m||"",total});
        inc(slotMap,`${meal.id}|${x.slotIndex}|${x.option.id}`,{meal:meal.name,slot:x.slot.t,item:x.option.n,note:x.option.m||"",total});
      });
      getOffPlanEntries(S,k,meal.id).forEach(entry=>{
        inc(optionMap,`off:${entry.foodId}`,{meal:meal.name,slot:"Off-plan",item:entry.name,note:`${entry.provider} · ${entry.grams} g`,total});
      });
      st.missing.forEach(slot=>inc(missingMap,`${meal.id}|${slot.t}`,{meal:meal.name,slot:slot.t,item:slot.t,total}));
    });
  });
  const avg=a=>a.length?Math.round(a.reduce((x,y)=>x+y,0)/a.length):0;
  const avg1=a=>a.length?(a.reduce((x,y)=>x+y,0)/a.length).toFixed(1):"0.0";
  const hit=fn=>pct(dayRows.filter(fn).length,total);
  const rewardDays=dayRows.filter(d=>d.reward).length;
  const completeMealDays=dayRows.filter(d=>d.meal.missing===0).length;
  const topFoods=ranked(optionMap).map(r=>Object.assign(r,{percent:pct(r.count,total)}));
  const slotChoices=ranked(slotMap).map(r=>Object.assign(r,{percent:pct(r.count,total)}));
  const slotWinners=[];
  const seenSlots=new Set();
  slotChoices.forEach(r=>{
    const key=`${r.meal}|${r.slot}`;
    if(seenSlots.has(key)) return;
    seenSlots.add(key);
    slotWinners.push(r);
  });
  const missingRows=ranked(missingMap).map(r=>Object.assign(r,{percent:pct(r.count,total)}));
  const mealConsistency=mealStats.map(r=>Object.assign({},r,{count:r.complete,percent:pct(r.complete,total)}))
    .sort((a,b)=>b.count-a.count || a.meal.localeCompare(b.meal,"en"));
  const mealSkipped=mealStats.map(r=>Object.assign({},r,{count:r.skipped,percent:pct(r.skipped,total)}))
    .sort((a,b)=>b.count-a.count || a.meal.localeCompare(b.meal,"en"));
  const stepsDaysOK=k=>{ const v=stepsFor(k); return v!==null && v>=STEP_GOAL; };
  const streaks=[
    {metric:"Logging",item:"Longest logging streak",count:longestStreak(keys,()=>true),value:`${longestStreak(keys,()=>true)} days`},
    {metric:"Protein",item:"Longest protein-hit streak",count:longestStreak(keys,k=>dayStats(k).p>=TARGET_P),value:`${longestStreak(keys,k=>dayStats(k).p>=TARGET_P)} days`},
    {metric:"Steps",item:"Longest 8k-steps streak",count:longestStreak(keys,stepsDaysOK),value:`${longestStreak(keys,stepsDaysOK)} days`},
    {metric:"Water",item:"Longest water streak",count:longestStreak(keys,k=>dayStats(k).water>=WATER_GOAL),value:`${longestStreak(keys,k=>dayStats(k).water>=WATER_GOAL)} days`},
    {metric:"Perfect days",item:"Longest perfect-day streak",count:longestStreak(keys,k=>completionState(k).complete),value:`${longestStreak(keys,k=>completionState(k).complete)} days`}
  ];
  return {
    keys,totalDays:total,rewardDays,completeMealDays,topFoods,slotChoices,slotWinners,mealConsistency,mealSkipped,missingRows,streaks,
    metrics:[
      {label:"Days logged",value:total},
      {label:"Full-meal days",value:completeMealDays},
      {label:"Perfect days",value:rewardDays},
      {label:"Avg calories",value:avg(dayRows.map(d=>d.stats.kc))},
      {label:"Avg protein",value:avg(dayRows.map(d=>d.stats.p))+" g"},
      {label:"Avg water",value:avg1(dayRows.map(d=>d.stats.water))+" bottles"},
      {label:"Protein hit rate",value:hit(d=>d.stats.p>=TARGET_P)+"%"},
      {label:"Water hit rate",value:hit(d=>d.stats.water>=WATER_GOAL)+"%"}
    ]
  };
}
function rankHTML(rows, emptyText, limit=5){
  if(!rows.length) return `<p class="empty-analytics">${emptyText}</p>`;
  return rows.slice(0,limit).map((r,i)=>{
    const title=esc(r.title||r.item||r.meal||r.metric);
    const sub=esc(r.sub || [r.meal,r.slot].filter(Boolean).join(" · ") || r.note || "");
    const side=r.value || `${r.count} / ${r.total}${r.percent!==undefined?` · ${r.percent}%`:""}`;
    return `<div class="rank-row"><span class="place">${i+1}</span><span><b>${title}</b>${sub?`<small>${sub}</small>`:""}</span><span class="pct">${esc(side)}</span></div>`;
  }).join("");
}
function renderAnalytics(){
  const a=buildAnalytics();
  document.getElementById("anaDays").textContent=a.totalDays?`${a.totalDays} days`:"Not yet";
  document.getElementById("anaMetrics").innerHTML=a.totalDays
    ? a.metrics.map(m=>`<div class="metric"><b>${esc(m.value)}</b><span>${esc(m.label)}</span></div>`).join("")
    : `<p class="empty-analytics">Not enough logged days to analyse yet.</p>`;
  document.getElementById("anaFoods").innerHTML=rankHTML(a.topFoods,"No food choices yet.",5);
  document.getElementById("anaSlots").innerHTML=rankHTML(a.slotWinners,"Not enough swaps yet.",8);
  const mealRows=[
    ...a.mealConsistency.slice(0,2).map(r=>({title:`Most consistent: ${r.meal}`,sub:"logged complete",count:r.count,total:r.total,percent:r.percent})),
    ...a.mealSkipped.slice(0,2).map(r=>({title:`Most skipped: ${r.meal}`,sub:"not logged at all",count:r.count,total:r.total,percent:r.percent}))
  ];
  document.getElementById("anaMeals").innerHTML=rankHTML(mealRows,"No meals to analyse yet.",4);
  document.getElementById("anaMissing").innerHTML=rankHTML(a.missingRows.map(r=>Object.assign({},r,{title:`${r.meal}: ${r.slot}`,sub:"skipped"})),"No skipped components.",5);
  document.getElementById("anaStreaks").innerHTML=rankHTML(a.streaks.map(r=>Object.assign({},r,{title:r.item,sub:r.metric,total:""})),"No streaks yet.",5);
  document.getElementById("anaShare").disabled=!a.totalDays;
}
function toAnalyticsCSV(){
  const a=buildAnalytics();
  const rows=[["section","rank","metric","meal","slot","item","count","total","percent","value","note"].join(",")];
  const add=(section,rank,metric,meal,slot,item,count,total,percent,value,note)=>{
    rows.push([section,rank,metric,meal,slot,item,count,total,percent,value,note].map(csvCell).join(","));
  };
  a.metrics.forEach((m,i)=>add("summary",i+1,m.label,"","","","","","",m.value,""));
  a.topFoods.forEach((r,i)=>add("food_options",i+1,"selected_count",r.meal,r.slot,r.item,r.count,r.total,r.percent,"",r.note));
  a.slotChoices.forEach((r,i)=>add("slot_choices",i+1,"selected_count",r.meal,r.slot,r.item,r.count,r.total,r.percent,"",r.note));
  a.mealConsistency.forEach((r,i)=>add("meal_consistency",i+1,"complete_days",r.meal,"","",r.count,r.total,r.percent,"",""));
  a.mealSkipped.forEach((r,i)=>add("meal_skipped",i+1,"skipped_days",r.meal,"","",r.count,r.total,r.percent,"",""));
  a.missingRows.forEach((r,i)=>add("missing_components",i+1,"missing_days",r.meal,r.slot,r.item,r.count,r.total,r.percent,"",""));
  a.streaks.forEach((r,i)=>add("streaks",i+1,r.metric,"","",r.item,r.count,"","",r.value,""));
  return rows.join("\r\n");
}
function reportSummary(){
  const keys=allKeys();
  if(!keys.length) return null;
  const logged=keys.filter(k=>dayStats(k).mealsLogged>0);
  const st=logged.map(dayStats);
  const avg=a=>a.length?Math.round(a.reduce((x,y)=>x+y,0)/a.length):0;
  const stepVals=S.st.map(e=>e.v);
  return {
    from:keys[0], to:keys[keys.length-1], days:logged.length,
    kcal:avg(st.map(s=>s.kc)), prot:avg(st.map(s=>s.p)),
    hit:st.filter(s=>s.p>=158).length,                       // ≥90% of the 175 g target
    water:logged.length?(st.reduce((a,s)=>a+s.water,0)/logged.length).toFixed(1):"0",
    steps:stepVals.length?Math.round(stepVals.reduce((a,b)=>a+b,0)/stepVals.length):null,
    w:last(S.w), a7:avgWindow(S.w,7), cs:last(S.cs), csFirst:S.cs.length?S.cs[0]:null
  };
}
function reportText(){
  const r=reportSummary();
  if(!r) return "Nothing logged yet.";
  const L=[`Cut report — ${r.from} to ${r.to}`,
    `Days logged: ${r.days}`,
    `Avg calories: ${r.kcal} (target 1450)`,
    `Avg protein: ${r.prot} g (target 175)`,
    `Days protein was hit: ${r.hit} of ${r.days}`,
    `Avg water: ${r.water} bottles`];
  if(r.steps!==null) L.push(`Avg steps: ${r.steps.toLocaleString()} (goal 8,000)`);
  if(r.w)  L.push(`Last weight: ${r.w.v.toFixed(1)} kg${r.a7?` · 7-day avg ${r.a7.toFixed(1)}`:""} — start 93.0`);
  if(r.cs) L.push(`Last waist: ${r.cs.v} cm${r.csFirst&&r.csFirst.d!==r.cs.d?` — start ${r.csFirst.v}`:""}`);
  return L.join("\n");
}
function renderReport(){
  const el=document.getElementById("repSum"), r=reportSummary();
  if(!r){ el.innerHTML=`<div><span>Nothing logged yet</span></div>`; return; }
  const rows=[["Days logged",r.days],["Avg calories",r.kcal],
    ["Avg protein",r.prot+" g"],["Protein-hit days",`${r.hit} / ${r.days}`],
    ["Avg water",r.water+" bottles"]];
  if(r.steps!==null) rows.push(["Avg steps",r.steps.toLocaleString()]);
  el.innerHTML=rows.map(([a,b])=>`<div><span>${a}</span><span>${b}</span></div>`).join("");
}
/* BOM keeps Excel happy with the encoding */
const csvBlob = () => new Blob(["\uFEFF"+toCSV()],{type:"text/csv;charset=utf-8"});
const analyticsBlob = () => new Blob(["\uFEFF"+toAnalyticsCSV()],{type:"text/csv;charset=utf-8"});
const foodEntriesBlob = () => new Blob(["\uFEFF"+toFoodEntriesCSV()],{type:"text/csv;charset=utf-8"});

async function shareReport(){
  if(!allKeys().length) return toast("Nothing to send yet");
  const fname=`omar-cut-${todayISO}.csv`;
  // 1) native share sheet — straight into WhatsApp
  try{
    const files=[new File([csvBlob()], fname, {type:"text/csv"})];
    if(getOffPlanEntries(S,todayISO).length || Object.keys(S.offPlan||{}).length) files.push(new File([foodEntriesBlob()],`omar-food-entries-${todayISO}.csv`,{type:"text/csv"}));
    if(navigator.canShare && navigator.canShare({files})){
      await navigator.share({files, title:"Cut report", text:reportText()});
      return;
    }
  }catch(e){ if(e && e.name==="AbortError") return; }
  // 2) plain download
  try{
    const url=URL.createObjectURL(csvBlob());
    const a=document.createElement("a"); a.href=url; a.download=fname;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(()=>URL.revokeObjectURL(url),2000);
    toast("File saved"); return;
  }catch(e){}
  toast("Couldn't save the file");
}
function downloadFoodEntries(){
  if(!Object.keys(S.offPlan||{}).length) return toast("No off-plan foods logged yet");
  downloadBlob(foodEntriesBlob(),`omar-food-entries-${todayISO}.csv`);
  toast("Food entries file saved");
}
function downloadBlob(blob,filename){
  const url=URL.createObjectURL(blob), a=document.createElement("a");
  a.href=url; a.download=filename; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(()=>URL.revokeObjectURL(url),2000);
}
async function shareAnalyticsReport(){
  if(!buildAnalytics().totalDays) return toast("No food data to analyse yet");
  const fname=`omar-analytics-${todayISO}.csv`;
  try{
    const file=new File([analyticsBlob()], fname, {type:"text/csv"});
    if(navigator.canShare && navigator.canShare({files:[file]})){
      await navigator.share({files:[file], title:"Cut analytics", text:"Food choice analytics"});
      return;
    }
  }catch(e){ if(e && e.name==="AbortError") return; }
  try{
    const url=URL.createObjectURL(analyticsBlob());
    const a=document.createElement("a"); a.href=url; a.download=fname;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(()=>URL.revokeObjectURL(url),2000);
    toast("Analytics file saved"); return;
  }catch(e){}
  toast("Couldn't save the analytics");
}
document.getElementById("repShare").onclick=shareReport;
document.getElementById("anaShare").onclick=shareAnalyticsReport;
document.getElementById("foodEntriesExport").onclick=downloadFoodEntries;
document.getElementById("backupExport").onclick=()=>{downloadBackup(S,APP_VERSION);toast("Backup saved");};
document.getElementById("backupImport").onclick=()=>document.getElementById("backupFile").click();
document.getElementById("backupFile").onchange=async event=>{
  const file=event.target.files?.[0]; event.target.value="";
  if(!file) return;
  try{
    const restored=parseBackup(await file.text());
    if(!confirm("Restore this backup? A safety copy of your current data will download first.")) return;
    downloadBackup(S,APP_VERSION);
    S=restored; await stateStore.save(S); renderAll(); toast("Backup restored");
  }catch(error){toast(error.message||"Backup could not be restored");}
};
document.getElementById("rankHelp").onclick=openRankInfo;
document.getElementById("rankInfoClose").onclick=closeRankInfo;
document.getElementById("rankInfo").onclick=e=>{ if(e.target===e.currentTarget) closeRankInfo(); };
document.getElementById("rankCelebrateClose").onclick=closeRankCelebration;
document.getElementById("rankCelebrate").onclick=e=>{ if(e.target===e.currentTarget) closeRankCelebration(); };
document.getElementById("celebrateClose").onclick=closeCelebration;
document.getElementById("celebrate").onclick=e=>{ if(e.target===e.currentTarget) closeCelebration(); };

/* ---------------- nav ---------------- */
document.querySelectorAll("nav button").forEach(b=>b.onclick=()=>{
  document.querySelectorAll("nav button").forEach(x=>x.classList.remove("on"));
  document.querySelectorAll(".view").forEach(v=>v.classList.remove("on"));
  b.classList.add("on"); document.getElementById(b.dataset.view).classList.add("on");
  window.scrollTo({top:0});
});
document.getElementById("prev").onclick=()=>{ cur=shiftKey(cur,-1); renderAll(); };
document.getElementById("next").onclick=()=>{ if(cur!==todayISO){ cur=shiftKey(cur,1); renderAll(); } };

/* ---------------- boot ---------------- */
function renderAll(){ renderHeader(); renderRankCard(); renderReward(); renderMeals(); renderWater(); renderMeas(); renderReport(); renderAnalytics(); }

function updateCatalogProgress(progress){
  const status=document.getElementById("catalogStatus"), version=document.getElementById("catalogVersion");
  if(status){status.textContent=progress.message;status.classList.add("catalog-progress");}
  if(version) version.textContent=progress.status==="ready"?getActiveCatalogVersion():getActiveCatalogVersion()==="core-1"?"Core":"Installing";
}

function bootFail(err){
  const box=document.createElement("div");
  box.style.cssText="margin:16px;padding:16px;border-radius:14px;background:#FBEFEE;"+
    "border-inline-start:3px solid #8E2A24;font-size:15px;color:#181D1B";
  box.innerHTML='<b style="display:block;color:#8E2A24;margin-bottom:6px">Something broke on launch</b>'+
    'Close the page and open it again. If it keeps happening, check the console.'+
    '<div style="margin-top:8px;font-size:12px;color:#6A736E;direction:ltr;text-align:left">'+
    String(err&&err.message||err)+'</div>';
  (document.querySelector("main")||document.body).prepend(box);
}

try{
  document.getElementById("appVersion").textContent=`v${APP_VERSION}`;
  await initializeCatalog(updateCatalogProgress);
  document.getElementById("catalogVersion").textContent=getActiveCatalogVersion();
  renderAll();
  if("serviceWorker" in navigator && location.protocol!=="file:") void navigator.serviceWorker.register("/sw.js").catch(()=>{});
  setInterval(refreshToday, 60000);
  document.addEventListener("visibilitychange", ()=>{ if(!document.hidden) refreshToday(); });
  window.addEventListener("focus", refreshToday);
  if(!storageWorks){
    setTimeout(()=>toast("Logs won't be saved here — open the file in a real browser"),700);
  }
}catch(err){ bootFail(err); }
window.addEventListener("error", e=>{ if(!document.querySelector("#meals").children.length) bootFail(e.error||e.message); });
